import React from 'react';
import { Lock, Heart, Diamond, Star, Clock, Check, ChevronRight } from 'lucide-react';
import { AngelSkin } from './AngelSkinsShop';
interface SkinCardProps {
  skin: AngelSkin;
  onClick: () => void;
  onUnlock: () => void;
}
export const SkinCard: React.FC<SkinCardProps> = ({
  skin,
  onClick,
  onUnlock
}) => {
  // Helper function to render the rarity badge
  const renderRarityBadge = (rarity: string) => {
    const colors = {
      Common: 'bg-gray-500',
      Rare: 'bg-blue-500',
      Epic: 'bg-purple-500',
      Legendary: 'bg-gradient-to-r from-yellow-500 to-amber-600'
    };
    const colorClass = colors[rarity as keyof typeof colors] || colors.Common;
    return <div className={`${colorClass} text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center`} data-id="element-19507">
        <Star size={12} className="mr-1" fill="currentColor" data-id="element-19508" />
        {rarity}
      </div>;
  };
  // Helper function to render currency icon
  const renderCurrencyIcon = (type: string) => {
    return type === 'Love Tokens' ? <Heart size={16} className="text-pink-500" fill="currentColor" data-id="element-19509" /> : <Diamond size={16} className="text-blue-400" data-id="element-19510" />;
  };
  return <div className="bg-white dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-300" data-id="element-19511">
      {/* Card image */}
      <div className="relative w-full aspect-[4/3] cursor-pointer" onClick={onClick} data-id="element-19512">
        <img src={skin.preview_image_url} alt={skin.skin_name} className={`w-full h-full object-cover ${skin.status === 'Locked' ? 'grayscale opacity-70' : ''}`} data-id="element-19513" />
        {/* Locked overlay */}
        {skin.status === 'Locked' && <div className="absolute inset-0 bg-black/50 flex items-center justify-center" data-id="element-19514">
            <Lock size={32} className="text-white opacity-80" data-id="element-19515" />
          </div>}
        {/* Status indicators */}
        <div className="absolute top-3 left-3 flex flex-col gap-2" data-id="element-19516">
          <div className="bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center" data-id="element-19517">
            <span className="mr-1" data-id="element-19518">👼</span>
            {skin.angel_name}
          </div>
          {skin.status === 'Active' && <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center" data-id="element-19519">
              <Check size={12} className="mr-1" data-id="element-19520" />
              Active
            </div>}
        </div>
        {/* FOMO label */}
        {skin.fomo_label && <div className="absolute top-3 right-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white text-xs px-2 py-1 rounded-full" data-id="element-19521">
            {skin.fomo_label}
          </div>}
        {/* Countdown timer */}
        {skin.time_limited && skin.countdown && <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg flex items-center" data-id="element-19522">
            <Clock size={12} className="mr-1" data-id="element-19523" />
            {skin.countdown}
          </div>}
        {/* Rarity badge */}
        <div className="absolute bottom-3 left-3" data-id="element-19524">
          {renderRarityBadge(skin.rarity)}
        </div>
      </div>
      {/* Card content */}
      <div className="p-4" data-id="element-19525">
        <div className="flex items-center justify-between mb-2" data-id="element-19526">
          <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-19527">
            {skin.skin_name}
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2" data-id="element-19528">
          {skin.short_description}
        </p>
        <div className="flex items-center justify-between" data-id="element-19529">
          <div className="flex items-center" data-id="element-19530">
            {renderCurrencyIcon(skin.currency_type)}
            <span className="ml-1 font-bold text-gray-900 dark:text-white" data-id="element-19531">
              {skin.price}
            </span>
          </div>
          {skin.status === 'Locked' ? <button onClick={e => {
          e.stopPropagation();
          onUnlock();
        }} className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-white text-xs flex items-center hover:shadow-md hover:shadow-pink-500/20 transition-all" data-id="element-19532">
              Unlock
              <ChevronRight size={14} className="ml-1" data-id="element-19533" />
            </button> : skin.status === 'Unlocked' ? <button onClick={e => {
          e.stopPropagation();
          onUnlock();
        }} className="px-3 py-1 bg-gradient-to-r from-green-600 to-teal-600 rounded-full text-white text-xs flex items-center hover:shadow-md hover:shadow-green-500/20 transition-all" data-id="element-19534">
              Activate
              <ChevronRight size={14} className="ml-1" data-id="element-19535" />
            </button> : <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full text-xs flex items-center" data-id="element-19536">
              <Check size={12} className="mr-1" data-id="element-19537" />
              Active
            </div>}
        </div>
      </div>
    </div>;
};